import axios from 'axios';
import { authService } from './auth';

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function resolveApiBaseUrl(): string {
  const configured = (import.meta as any).env?.VITE_API_URL as string | undefined;
  if (configured && configured.trim()) {
    return normalizeBaseUrl(configured.trim());
  }

  if (typeof window === 'undefined') {
    return 'http://localhost:8080';
  }

  const hostname = window.location.hostname.toLowerCase();
  const origin = window.location.origin;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8080';
  }

  const hostedMap: Array<{ suffix: string; api: string }> = [
    { suffix: 'admin.d.xtnd.legal', api: 'https://api.d.xtnd.legal' },
    { suffix: 'admin.u.xtnd.legal', api: 'https://api.u.xtnd.legal' },
    { suffix: 'admin.uat.xtnd.legal', api: 'https://api.u.xtnd.legal' },
    { suffix: 'admin.p.xtnd.legal', api: 'https://api.p.xtnd.legal' },
    { suffix: 'admin.xtnd.legal', api: 'https://api.p.xtnd.legal' },
  ];

  for (const mapping of hostedMap) {
    if (hostname === mapping.suffix || hostname.endsWith(`.${mapping.suffix}`)) {
      return mapping.api;
    }
  }

  return `${normalizeBaseUrl(origin)}/api`;
}

const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
apiClient.interceptors.request.use(async config => {
  // Skip auth for certain endpoints or when explicitly disabled
  const skipAuth = config.headers?.['skip-auth'] === 'true';

  if (!skipAuth) {
    try {
      const accessToken = await authService.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    } catch (error) {
      console.error('Failed to get access token:', error);
      // Continue without token - let the server handle unauthorized requests
    }
  }

  // Remove the skip-auth header before sending request
  delete config.headers?.['skip-auth'];

  return config;
});

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Handle 401 Unauthorized responses
    if (error.response?.status === 401) {
      // Clear stored tokens and redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('expiredAt');
      localStorage.removeItem('user');

      // Redirect to login page
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Helper function for public endpoints (no authentication required)
const createPublicRequest = () => {
  return axios.create({
    baseURL: resolveApiBaseUrl(),
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export { apiClient, createPublicRequest };
